// src/components/WarbandGenerator.js
import React, { useState, useEffect, useRef } from "react";
import { PDFDownloadLink } from '@react-pdf/renderer';
import ForbiddenPsalmCardPDF from './ForbiddenPsalmCardPDF';
import ForbiddenPsalmRosterPDF from './ForbiddenPsalmRosterPDF';
import "./css/warband-card.css";

const ForbiddenPsalmGenerator = () => {
  const [warband, setWarband] = useState(null);
  const [warbandName, setWarbandName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isClient, setIsClient] = useState(false);

  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    setIsClient(true);
  }, []);

  const formatStat = (value) => (value > 0 ? `+${value}` : value);

  const fetchWarband = async () => {
    setLoading(true);
    setError(null);
    setUploadError(null);
    setWarband(null);
    setWarbandName("");

    try {
      const warbandResponse = await fetch(`${API_URL}/api/forbidden-psalm/warband`);
      if (!warbandResponse.ok) {
        const errorData = await warbandResponse.text();
        throw new Error(`Failed to fetch warband: ${warbandResponse.status} ${errorData}`);
      }
      const warbandData = await warbandResponse.json();

      if (!warbandData || typeof warbandData.warbandName !== 'string' || !Array.isArray(warbandData.warband)) {
        throw new Error("Invalid data format received from server.");
      }

      const warbandWithNotes = warbandData.warband.map(member => ({
        ...member,
        Notes: member.Notes || ""
      }));

      setWarband(warbandWithNotes);
      setWarbandName(warbandData.warbandName);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message);
      setWarband(null);
      setWarbandName("");
    } finally {
      setLoading(false);
    }
  };

  const createFilename = (name, extension, suffix = '') => {
    const baseName = name || 'Warband';
    const sanitizedName = baseName.replace(/\s+/g, '_');
    const prefix = extension === 'pdf' ? 'FP_' : '';
    const suffixString = suffix ? `_${suffix}` : '';
    return `${prefix}${sanitizedName}${suffixString}.${extension}`;
  };

  const downloadJson = () => {
    if (!warband) return;
    const dataToDownload = { warbandName: warbandName, warband: warband };
    const jsonString = JSON.stringify(dataToDownload, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = createFilename(warbandName, 'json');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUploadButtonClick = () => {
    setUploadError(null);
    setError(null);
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/json") {
        setUploadError("Invalid file type. Please upload a .json file.");
        setWarband(null);
        setWarbandName("");
        return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        if (typeof text !== 'string') {
            throw new Error("Failed to read file content.");
        }
        const parsedData = JSON.parse(text);

        if (
          !parsedData ||
          typeof parsedData.warbandName !== 'string' ||
          !Array.isArray(parsedData.warband)
        ) {
          throw new Error("Invalid JSON structure. Missing 'warbandName' (string) or 'warband' (array).");
        }

        const validatedWarband = parsedData.warband.map((card, index) => {
          if (!card || typeof card.name !== 'string' || typeof card.title !== 'string') {
            // Allow scrolls which might not have a name
            if(card.stats) { // Only throw if it's a character missing name/title
                 throw new Error(`Invalid structure in card at index ${index}. Missing name or title.`);
            }
          }
          const notesValue = typeof card.Notes === 'string' ? card.Notes : "";
          return {
            ...card,
            Notes: notesValue
          };
        });

        setWarband(validatedWarband);
        setWarbandName(parsedData.warbandName);
        setError(null);
        setUploadError(null);

      } catch (err) {
        console.error("Upload processing error:", err);
        setUploadError(`Error processing file: ${err.message}`);
        setWarband(null);
        setWarbandName("");
      }
    };

    reader.onerror = (e) => {
      console.error("File reading error:", e);
      setUploadError("Failed to read the selected file.");
      setWarband(null);
      setWarbandName("");
    };

    reader.readAsText(file);
  };


  return (
    <div className="container">
      <h1>Forbidden Psalm Random Warband Generator</h1>
      {/* Button Row */}
      <div>
        {/* ... Generate Button ... */}
        <button
          onClick={fetchWarband}
          className="generate-button"
          disabled={loading}
          style={{ marginRight: '10px' }}
        >
          {loading ? 'Generating...' : 'Generate Warband'}
        </button>
        {/* ... Hidden Input & Upload Button ... */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".json,application/json"
          style={{ display: 'none' }}
        />
        <button
          onClick={handleUploadButtonClick}
          className="generate-button"
          disabled={loading}
        >
          Upload Warband JSON
        </button>
      </div>

      {/* Loading/Error Display */}
      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {uploadError && <p style={{ color: "orange" }}>Upload Error: {uploadError}</p>}

      {/* Warband Name & Cards Web View */}
      {warbandName && <h2 className="warband-name">{warbandName}</h2>}
      {warband && (
        <div className="warband">
          {/* ... card mapping ... */}
           {warband.map((card, index) => (
            <div key={index} className="warband-card">
              <h2>{card.name} {card.title}</h2>
              {card.stats ? (
                <div>
                  <div className="stats-container">
                    <div className="stats-column">
                      <p><strong>Agility:   </strong> {formatStat(card.stats.Agility)}</p>
                      <p><strong>Presence:  </strong> {formatStat(card.stats.Presence)}</p>
                      <p><strong>Strength:  </strong> {formatStat(card.stats.Strength)}</p>
                      <p><strong>Toughness: </strong> {formatStat(card.stats.Toughness)}</p>
                    </div>
                    <div className="stats-column">
                      <p><strong>Health Points: </strong> {card.stats.HP}</p>
                      <p><strong>Movement:      </strong> {card.stats.Movement}</p>
                    </div>
                  </div>
                  <hr className="divider" />
                  <p><strong>Feats: </strong> {card.feats}</p>
                  <p><strong>Flaws: </strong> {card.flaws}</p>
                  <hr className="divider" />
                  <p><strong>Weapon: </strong> {card.weapon}</p>
                  <p><strong>Armor:  </strong> {card.armor}</p>
                  <hr className="divider" />
                  <p><strong>Equipment:</strong></p>
                  <ul className="equipment-list">
                    {Array.isArray(card.equipment) && card.equipment.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <hr className="divider" />
                  <p><strong>Notes: </strong> {card.Notes}</p>
                </div>
              ) : (
                <div>
                  <p><strong>Clean Scroll:   </strong> {card.cleanScroll}</p>
                  <p><strong>Unclean Scroll: </strong> {card.uncleanScroll}</p>
                   <hr className="divider" />
                   <p><strong>Notes: </strong> {card.Notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Download Buttons Container */}
      {isClient && warband && warband.length > 0 && (
        // --- MODIFIED LINE: Added justifyContent: 'center' ---
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {/* Fancy PDF Download Link */}
          <PDFDownloadLink
            key={warbandName + warband.length + '-fancy'}
            document={<ForbiddenPsalmCardPDF warband={warband} warbandName={warbandName} />}
            fileName={createFilename(warbandName, 'pdf', 'Cards')}
            className="generate-button"
          >
            {({ loading: pdfLoading }) => pdfLoading ? 'Generating Cards PDF...' : 'Download Cards PDF'}
          </PDFDownloadLink>

          {/* Condensed PDF Download Link */}
          <PDFDownloadLink
            key={warbandName + warband.length + '-condensed'}
            document={<ForbiddenPsalmRosterPDF warband={warband} warbandName={warbandName} />}
            fileName={createFilename(warbandName, 'pdf', 'Roster')}
            className="generate-button"
          >
            {({ loading: pdfLoading }) => pdfLoading ? 'Generating Roster PDF...' : 'Download Roster PDF'}
          </PDFDownloadLink>

          {/* JSON Download Button */}
          <button onClick={downloadJson} className="generate-button">
            Download JSON
          </button>
        </div>
      )}
    </div>
  );
};

export default ForbiddenPsalmGenerator;
